import type { DependencyMapNode } from './types';
import { ArdoqId, ReferenceDirection } from '@ardoq/api-types';
import { referencedNodes } from './utils';
import { isTruthy } from '@ardoq/common-helpers';
import { enhancedScopeDataOperations } from '@ardoq/scope-data';
import type { EnhancedScopeData } from '@ardoq/data-model';

const getComponentIdMaxParentDepthReducer =
  (scopeData: EnhancedScopeData) =>
  (maxDepth: number, currentComponentId: ArdoqId) => {
    let currentDepth = 0;
    let currentParentId = enhancedScopeDataOperations.getParentId(
      scopeData,
      currentComponentId
    );
    while (currentParentId) {
      currentDepth++;
      currentParentId = enhancedScopeDataOperations.getParentId(
        scopeData,
        currentParentId
      );
    }
    return Math.max(currentDepth, maxDepth);
  };

const getReferenceParentMaxDepthReducer = (scopeData: EnhancedScopeData) => {
  const componentIdMaxParentDepthReducer =
    getComponentIdMaxParentDepthReducer(scopeData);
  const referenceParentMaxDepthReducer = (
    maxDepth: number,
    currentNode: DependencyMapNode
  ): number => {
    // potential optimization: first collect all ids and filter to a unique set, then calculate max depth (avoid checking the same component id multiple times)
    const referencedComponentIds = [
      ...(currentNode.combinedReferences?.map(
        ({ direction, sourceId, targetId }) =>
          direction === ReferenceDirection.OUTGOING ? targetId : sourceId
      ) ?? []),
      ...(currentNode.incomingReferences?.map(
        ({ referenceIds }) =>
          enhancedScopeDataOperations.getReference(scopeData, referenceIds[0])
            ?.source
      ) ?? []),
      ...(currentNode.outgoingReferences?.map(
        ({ referenceIds }) =>
          enhancedScopeDataOperations.getReference(scopeData, referenceIds[0])
            ?.target
      ) ?? []),
    ];
    return Math.max(
      maxDepth,
      referencedComponentIds
        .filter(isTruthy)
        .reduce(componentIdMaxParentDepthReducer, 0),
      Array.from(referencedNodes(currentNode)).reduce(
        referenceParentMaxDepthReducer,
        0
      ),
      currentNode.children
        ? currentNode.children.reduce(referenceParentMaxDepthReducer, 0)
        : 0
    );
  };
  return referenceParentMaxDepthReducer;
};
export { getReferenceParentMaxDepthReducer };
